import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, CheckCircle, FileText } from 'lucide-react';

const FormSidebar = ({ 
  isOpen, 
  close, 
  formName, 
  formData, 
  currentStep, 
  setCurrentStep, 
  completedSteps = new Set()
}) => {
  const navigate = useNavigate();

  const handleStepClick = (stepIndex) => {
    setCurrentStep(stepIndex);
    // Remove scrollToTop call here since DynamicForm's useEffect will handle it
    if (close) close();
  };

  const handleDashboardClick = () => {
    navigate("/user-dashboard");
    if (close) close();
  };

  const handleTranscriptionClick = () => {
    navigate("/user/transcription-dashboard");
    if (close) close();
  };

  if (!formData?.form_form_steps) {
    return null;
  }

  return (
    <>
      <aside 
        className={`fixed top-16 left-0 w-full lg:w-80 bg-secondary border-r border-secondary-border h-[calc(100vh-4rem)] overflow-y-auto transform transition-transform duration-300 ease-in-out z-40 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{
          scrollBehavior: 'smooth',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch'
        }}
        onMouseEnter={(e) => {
          // Ensure this element can receive scroll events
          e.currentTarget.focus();
        }}
      >
        <div className="p-6">
          <h2 className="text-2xl font-bold text-foreground mb-8">
            My Form
          </h2>
          <nav>
            <ul className="space-y-3">
              {/* Dashboard Button - always visible for easy navigation back */}
              <li>
                <button
                  onClick={handleDashboardClick}
                  className="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-background border border-secondary-border"
                >
                  <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center mr-4">
                    <Home className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span>Dashboard</span>
                </button>
              </li>

              {/* Transcription Button */}
              <li>
                <button
                  onClick={handleTranscriptionClick}
                  className="w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-background border border-secondary-border"
                >
                  <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center mr-4">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span>Transcription</span>
                </button>
              </li>
              
              {/* Divider */}
              <li className="py-2">
                <div className="border-t border-secondary-border"></div>
              </li>

              {/* Dynamic Form Steps */}
              {formData.form_form_steps.map((step, index) => (
                <li key={step.id}>
                  <button
                    onClick={() => handleStepClick(index)}
                    className={`w-full flex items-center px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      index === currentStep
                        ? "bg-primary text-white shadow-sm"
                        : completedSteps.has(index)
                        ? "bg-background text-foreground border border-secondary-border"
                        : "text-muted-foreground hover:text-foreground hover:bg-background"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg mr-4 text-sm font-semibold
                      ${
                        index === currentStep
                          ? "bg-white/20 text-white"
                          : completedSteps.has(index)
                          ? "bg-primary text-white"
                          : "bg-background text-muted-foreground"
                      }`}
                    >
                      {completedSteps.has(index) ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="text-left flex-1">
                      <div className="font-semibold leading-tight">
                        {step.title}
                      </div>
                      {step.description && (
                        <div className={`text-xs mt-1 leading-tight ${
                          index === currentStep 
                            ? "text-white/80" 
                            : "text-muted-foreground"
                        }`}>
                          {step.description.length > 45 
                            ? `${step.description.substring(0, 45)}...` 
                            : step.description
                          }
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}


            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default FormSidebar; 